var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import clsx from 'clsx';
import { math } from '../utils/Polished';
import { withForwardedRef } from '../utils/helpers/reactHelpers';
import { ContextTabIndexState } from './Tabs';
const getBorderBtm = (props) => `solid ${props.theme.SizeBorderWidthTabsBottomBase} ${props.theme.ColorBorderTabsBase}`;
const getPaddingBtm = (props) => math(`${props.theme.SizePaddingTabsBase} - 0.0625rem`);
const getActivePaddingBtn = (props) => math(`${props.theme.SizePaddingTabsBase} - 0.125rem`);
const getActiveBorderBtm = (props) => `solid ${props.theme.SizeBorderWidthTabsBottomActive} ${props.theme.ColorBorderTabsActive}`;
const TabSC = styled.button `
  min-width: ${(props) => props.theme.SizeWidthTabsBase};
  height: ${(props) => props.theme.SizeHeightTabsBase};
  cursor: pointer;
  display: flex;
  flex-grow: 0;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;

  border-bottom: ${getBorderBtm};
  padding: ${(props) => props.theme.SizePaddingTabsBase};
  padding-bottom: ${getPaddingBtm};

  overflow: hidden;

  ._ndsTab__label {
    margin: 0;
    padding: 0;
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.ColorFontTabsBase};
    font-weight: ${(props) => props.theme.ContentFontWeightBold};
    font-size: ${(props) => props.theme.SizeFontS};
    line-height: ${(props) => props.theme.SizeLineHeightS};
    font-family: ${(props) => props.theme.FontFamilyBase};
  }

  /* full width */
  &._ndsTab--full-width {
    flex-grow: 1;
  }

  /* default */
  background: ${(props) => props.theme.ColorBackgroundTabsBase};

  &:hover {
    background: ${(props) => props.theme.ColorBackgroundTabsHover};
    border-bottom-color: ${(props) => props.theme.ColorBackgroundTabsHover};
  }

  &:active,
  &:focus {
    background: ${(props) => props.theme.ColorBackgroundTabsFocus};
    border-bottom-color: ${(props) => props.theme.ColorBackgroundTabsFocus};
    outline: 0;
  }

  /* active */
  &._ndsTab--active {
    &:hover {
      background: ${(props) => props.theme.ColorBackgroundTabsHover};
    }

    &:active,
    &:focus {
      background: ${(props) => props.theme.ColorBackgroundTabsFocus};
    }

    padding-bottom: ${getActivePaddingBtn};
    border-bottom: ${getActiveBorderBtm};

    background: ${(props) => props.theme.ColorBackgroundTabsActive};

    ._ndsTab__label {
      color: ${(props) => props.theme.ColorFontTabsActive};
    }
  }

  /* disabled */
  &._ndsTab--disabled {
    &:hover,
    &:active,
    &:focus,
    & {
      cursor: not-allowed;
      border-bottom-color: ${(props) => props.theme.ColorBorderTabsBase};

      background: ${(props) => props.theme.ColorBackgroundTabsDisabled};
      ._ndsTab__label {
        color: ${(props) => props.theme.ColorFontTabsDisabled};
      }
    }
  }
`;
const Tab = (props) => {
    const { children, forwardedRef, className: classNameProp, index, disabled, fullWidth } = props, others = __rest(props, ["children", "forwardedRef", "className", "index", "disabled", "fullWidth"]);
    const { activeIndex, onTabClick } = React.useContext(ContextTabIndexState);
    const isActive = activeIndex === index;
    const handleOnClick = React.useCallback((event) => {
        if (disabled) {
            return;
        }
        onTabClick(event);
    }, [disabled, onTabClick]);
    const handleKeyPress = React.useCallback((event) => {
        event.preventDefault();
        handleOnClick(event);
    }, [handleOnClick]);
    return (React.createElement(TabSC, Object.assign({ ref: forwardedRef, className: clsx(classNameProp, '_ndsTab', {
            '_ndsTab--active': isActive,
            '_ndsTab--disabled': disabled,
            '_ndsTab--full-width': fullWidth
        }), onClick: handleOnClick, "data-index": index, role: "tab", tabIndex: 0, onKeyPress: handleKeyPress }, others),
        React.createElement("span", { className: "_ndsTab__label" }, children)));
};
Tab.propTypes = {
    children: PropTypes.node,
    forwardedRef: PropTypes.any,
    className: PropTypes.string,
    index: PropTypes.number,
    disabled: PropTypes.bool,
    fullWidth: PropTypes.bool
};
Tab.defaultProps = {
    children: null,
    forwardedRef: null,
    className: undefined,
    index: 0,
    disabled: false,
    fullWidth: false
};
export default withForwardedRef(Tab);
//# sourceMappingURL=Tab.js.map